/**
 * MusicGenerationPage - AI music generation using Lyria 3.
 */

import { useCallback, useEffect, useState, memo } from 'react';
import { Music2 } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { ToastContainer } from '../../components/Toast';
import { LoginModal } from '../../components/auth';
import { ElapsedTimeDisplay } from '../../components/common';
import { TokenEstimateCard } from '../../components/tokens';
import { useMusicGeneration } from '../../hooks/useMusicGeneration';
import { MusicModelSelector } from './MusicModelSelector';
import { MusicResult } from './MusicResult';
import { STORAGE_KEYS } from '../../constants/storage';
import type { MusicJobResult } from '../../services/polling/musicPollAdapter';
import type { MusicVideoContext } from '../VideoPage/components/types';
import { costGateBlockLabel, COST_GATE_OPEN, type CostGateStatus } from '../../utils/costGate';

export const MusicGenerationPage = memo(function MusicGenerationPage() {
  const { t } = useTranslation();
  useAuth();
  const { isLoginModalOpen, loginMessage, closeLoginModal, requireAuth } = useRequireAuth();
  const {
    config,
    setConfig,
    isGenerating,
    progress,
    startedAt,
    expectedSeconds,
    result,
    error,
    models,
    generateMusic,
    reset,
    useCustomLyrics,
    setUseCustomLyrics,
    lyrics,
    setLyrics,
    isDraftingLyrics,
    lyricsError,
    draftLyrics,
    currentJobId,
  } = useMusicGeneration();
  const { toasts, showToast, removeToast } = useToast();
  const [costGate, setCostGate] = useState<CostGateStatus>(COST_GATE_OPEN);

  const handleCreateVideoClip = useCallback(
    (musicResult: MusicJobResult) => {
      // Use the live job ID from useMusicGeneration; fall back to a timestamp key
      // only when currentJobId is unexpectedly null (should not happen on a completed job).
      const resolvedJobId = currentJobId ?? `unknown-${Date.now()}`;

      const ctx: MusicVideoContext = {
        musicJobId: resolvedJobId,
        audioUrl: musicResult.audioUrl,
        lyrics: musicResult.lyrics,
        durationSeconds: musicResult.durationSeconds,
        model: musicResult.model,
        prompt: null,
      };

      try {
        localStorage.setItem(STORAGE_KEYS.MUSIC_VIDEO_CONTEXT, JSON.stringify(ctx));
      } catch {
        // localStorage not available — proceed anyway, VideoPage will fall back to standard mode
      }

      // Navigate to the video page (App.tsx listens for this event)
      window.dispatchEvent(new CustomEvent('navigate-to-mode', { detail: 'video' }));
    },
    [currentJobId]
  );

  const selectedModel = models.find((m) => m.id === config.model);
  const estimatedTokens = selectedModel?.estimated_tokens ?? 0;

  const handleGenerate = useCallback(async () => {
    requireAuth(
      async () => {
        showToast(
          'info',
          t('music.notifications.startingTitle', { defaultValue: 'Starting Music Generation' }),
          t('music.notifications.startingMessage', {
            defaultValue: 'Your track is being generated...',
          }),
          3000
        );
        await generateMusic();
      },
      t('auth.loginRequired.music', { defaultValue: 'Please log in to generate music' })
    );
  }, [requireAuth, showToast, t, generateMusic]);

  useEffect(() => {
    if (error) {
      showToast(
        'error',
        t('music.notifications.errorTitle', { defaultValue: 'Music Generation Failed' }),
        error
      );
    }
  }, [error, showToast, t]);

  useEffect(() => {
    if (result?.audioUrl) {
      showToast(
        'success',
        t('music.notifications.successTitle', { defaultValue: 'Music Ready!' }),
        selectedModel?.display_name ?? config.model
      );
    }
  }, [result, showToast, t, selectedModel, config.model]);

  const showForm = !isGenerating && !result;

  return (
    <main
      className="w-full px-3 sm:px-6 lg:px-8 py-4 sm:py-8 space-y-6"
      data-testid="music-generation-page"
    >
      {/* Hero */}
      {showForm && (
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center space-x-3">
            <Music2 className="h-8 w-8 text-purple-300" />
            <h1 className="text-2xl sm:text-3xl font-bold text-white">
              {t('music.hero.title', { defaultValue: 'AI Music Generation' })}
            </h1>
          </div>
          <p className="text-white/60 text-sm sm:text-base max-w-xl mx-auto">
            {t('music.hero.subtitle', {
              defaultValue: 'Generate songs with vocals and timed lyrics using Lyria 3',
            })}
          </p>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="max-w-2xl mx-auto bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-4 sm:p-6 space-y-5">
          {/* Prompt */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-white/70">
              {t('music.form.promptLabel', { defaultValue: 'Describe the music' })}
            </label>
            <textarea
              value={config.prompt}
              onChange={(e) => setConfig((prev) => ({ ...prev, prompt: e.target.value }))}
              placeholder={t('music.form.promptPlaceholder', {
                defaultValue:
                  'e.g. happy upbeat acoustic guitar, cinematic orchestral piece, lo-fi study beat...',
              })}
              maxLength={2000}
              rows={4}
              className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/40 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500/50 resize-none"
            />
            <p className="text-xs text-white/40 text-right">{config.prompt.length} / 2000</p>
          </div>

          {/* Custom-lyrics toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-white/70">
              {t('music.form.customLyricsToggle', { defaultValue: 'Provide custom lyrics' })}
            </span>
            <button
              onClick={() => setUseCustomLyrics((v) => !v)}
              disabled={isGenerating}
              className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                useCustomLyrics ? 'bg-purple-600' : 'bg-white/20'
              }`}
              role="switch"
              aria-checked={useCustomLyrics}
              aria-label={t('music.form.customLyricsToggle', {
                defaultValue: 'Provide custom lyrics',
              })}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  useCustomLyrics ? 'translate-x-5' : ''
                }`}
              />
            </button>
          </div>

          {/* Custom-lyrics section (shown when toggle is ON) */}
          {useCustomLyrics && (
            <section className="bg-black/30 border border-white/10 rounded-lg p-3 sm:p-4 space-y-3">
              <label className="block text-sm font-medium text-white/70">
                {t('music.form.lyricsLabel', { defaultValue: 'Lyrics' })}
              </label>
              <textarea
                value={lyrics}
                onChange={(e) => setLyrics(e.target.value)}
                placeholder={t('music.form.lyricsPlaceholder', {
                  defaultValue: '[Verse 1]\nThe sun rises over the hills...\n\n[Chorus]\n...',
                })}
                rows={6}
                className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/40 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500/50 resize-none"
              />

              {/* Generate-lyrics button */}
              <button
                type="button"
                onClick={draftLyrics}
                disabled={!config.prompt.trim() || isDraftingLyrics || isGenerating}
                className="btn-secondary px-6 py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDraftingLyrics
                  ? t('music.form.generatingLyrics', { defaultValue: 'Drafting lyrics...' })
                  : t('music.form.generateLyricsButton', { defaultValue: 'Generate lyrics' })}
              </button>

              {/* Help text */}
              <p className="text-xs text-white/50">
                {t('music.form.lyricsHelp', {
                  defaultValue: 'Lyria will follow these lyrics closely but may adjust phrasing',
                })}
              </p>

              {/* Lyrics error */}
              {lyricsError && <p className="text-xs text-red-400">{lyricsError}</p>}
            </section>
          )}

          {/* Model selector */}
          <MusicModelSelector
            models={models}
            selectedModel={config.model}
            onChange={(modelId) => setConfig((prev) => ({ ...prev, model: modelId }))}
            disabled={isGenerating}
          />

          {/* Cost estimate — reuses TokenEstimateCard with the registry's static price */}
          {estimatedTokens > 0 && (
            <TokenEstimateCard
              staticEstimate={estimatedTokens}
              onEstimateChange={({ status }) => setCostGate(status)}
            />
          )}

          {/* Generate button — shared btn-primary, matches Audio/Video tabs */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={isGenerating || !config.prompt.trim() || costGate !== 'affordable'}
              className="btn-primary px-8 py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Music2 className="h-5 w-5" />
              <span>
                {costGateBlockLabel(costGate, t) ??
                  t('music.form.generateButton', { defaultValue: 'Generate music' })}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Progress */}
      {isGenerating && (
        <div className="max-w-2xl mx-auto bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-4 sm:p-6 space-y-4">
          <div className="flex items-center space-x-2">
            <div className="spinner" />
            <span className="text-white font-medium">
              {t('music.progress.generating', { defaultValue: 'Generating your track...' })}
            </span>
          </div>

          {/* Progress bar */}
          <div className="space-y-1">
            <div className="flex justify-between text-sm text-white/50">
              <span>{t('common.progress', { defaultValue: 'Progress' })}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
              <div
                className="bg-gradient-to-r from-purple-500 to-purple-300 h-full rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Elapsed time */}
          {startedAt && (
            <ElapsedTimeDisplay
              startedAt={startedAt}
              serverElapsedSeconds={null}
              estimatedRemainingSeconds={
                isGenerating
                  ? Math.max(
                      0,
                      expectedSeconds - (Date.now() - new Date(startedAt).getTime()) / 1000
                    )
                  : null
              }
              showEstimate
              size="sm"
            />
          )}
        </div>
      )}

      {/* Result */}
      {result && !isGenerating && (
        <div className="max-w-2xl mx-auto">
          <MusicResult result={result} onReset={reset} onCreateVideoClip={handleCreateVideoClip} />
        </div>
      )}

      {/* Login Modal */}
      <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} message={loginMessage} />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </main>
  );
});

export default MusicGenerationPage;
